import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { createSafeCloseHandler } from '../../utils/modalUtils';

interface EditUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpdateUser: (userData: any) => Promise<void>;
  user: any;
  roles: any[];
  escalationLevels?: any[];
}

export const EditUserModal: React.FC<EditUserModalProps> = ({
  isOpen,
  onClose,
  onUpdateUser,
  user,
  roles,
  escalationLevels = []
}) => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    position: '',
    department: '',
    project_role_id: '',
    escalation_level_id: ''
  });
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (user) {
      setFormData({
        first_name: user.user?.first_name || '',
        last_name: user.user?.last_name || '',
        email: user.user?.email || '',
        phone: user.user?.phone || '',
        position: user.position || '',
        department: user.department || '',
        project_role_id: user.project_role_id || '',
        escalation_level_id: user.escalation_level_id || ''
      });
    }
  }, [user]);

  // Create safe close handler to prevent accidental loss of data
  const handleSafeClose = createSafeCloseHandler(
    formData,
    onClose
  );

  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleSafeClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscapeKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isOpen, handleSafeClose]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    setIsLoading(true);
    try {
      // Prepare data according to API specification
      const updateData = {
        id: user.id, // Required: record ID
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        phone: formData.phone,
        position: formData.position,
        department: formData.department,
        project_role_id: formData.project_role_id,
        escalation_level_id: formData.escalation_level_id,
        status: user.status
      };

      await onUpdateUser(updateData);
      toast.success('User updated successfully');
      onClose();
    } catch (error) {
      toast.error('Failed to update user');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-white bg-opacity-80 flex items-center justify-center z-50"
      onClick={handleSafeClose}
    >
      <button
        onClick={handleSafeClose}
        className="fixed top-4 right-4 z-[60] text-[#aaa] hover:text-[#555] p-2"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
      
      <div 
        className="bg-white rounded-lg w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto border border-[#eaeaea]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-6 border-b border-[#eaeaea]">
          <h2 className="text-lg font-semibold text-[#171717]">Edit User</h2>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-[#444] mb-1">
                First Name *
              </label>
              <input
                type="text"
                name="first_name"
                value={formData.first_name}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-[#eaeaea] rounded-lg focus:outline-none focus:ring-0"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[#444] mb-1">
                Last Name *
              </label>
              <input
                type="text"
                name="last_name"
                value={formData.last_name}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-[#eaeaea] rounded-lg focus:outline-none focus:ring-0"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[#444] mb-1">
                Email *
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-[#eaeaea] rounded-lg focus:outline-none focus:ring-0"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[#444] mb-1">
                Phone
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-[#eaeaea] rounded-lg focus:outline-none focus:ring-0"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[#444] mb-1">
                Position
              </label>
              <input
                type="text"
                name="position"
                value={formData.position}
                onChange={handleInputChange}
                placeholder="e.g. Project Manager, Engineer"
                className="w-full px-3 py-2 border border-[#eaeaea] rounded-lg focus:outline-none focus:ring-0"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[#444] mb-1">
                Role
              </label>
              <select
                name="project_role_id"
                value={formData.project_role_id}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-[#eaeaea] rounded-lg focus:outline-none focus:ring-0"
              >
                <option value="">Select a role</option>
                {(roles || []).map((role) => (
                  <option key={role.id} value={role.id}>
                    {role.name?.replace('Agency', 'Company') || role.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-[#444] mb-1">
                Escalation Level
              </label>
              <select
                name="escalation_level_id"
                value={formData.escalation_level_id}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-[#eaeaea] rounded-lg focus:outline-none focus:ring-0"
              >
                <option value="">Select a level</option>
                {(escalationLevels || []).map((level) => (
                  <option key={level.id} value={level.id}>
                    {level.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-[#444] mb-1">
                Department
              </label>
              <input
                type="text"
                name="department"
                value={formData.department}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-[#eaeaea] rounded-lg focus:outline-none focus:ring-0"
              />
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 mt-6 pt-6 border-t border-[#eaeaea]">
            <button
              type="button"
              onClick={handleSafeClose}
              className="px-4 py-2 text-sm text-[#555] border border-[#eaeaea] rounded hover:bg-[#fafafa] transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 text-sm bg-[#171717] text-white rounded hover:bg-[#333] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isLoading ? 'Updating...' : 'Update User'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}; 